import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { clearError, clearSuccess, resetAll, resetVerification, setAuthFlow, setPhoneVerified, setUserStatus } from '../../../core/store/slices/auth.slice';
import { clearAllApiErrors } from '../../../core/store/slices/apiError.slice';
import { useAuth } from '../hooks/useAuth';
import { useSnackbar } from '../../../core/hooks/useSnackbar';
import { ErrorSource } from '../../../core/constant/model/api/api';
import FormContainer from '../../../core/components/layout/FormContainer';
import AuthTabs from './AuthTabs';
import PhoneInputStep from './PhoneInputStep';
import VerificationCodeStep from './VerificationCodeStep';
import EmailInitiationStep from './EmailInitiationStep';
import ApiErrorLabel from '../../../core/components/ui/ApiErrorLabel';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography, useTheme } from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { RootState } from '../../../core/store';
import { DEACTIVATED_ACCOUNT_ERROR_MESSAGE, UserStatus } from '../../../core/constant/constant';
import { openMailBox } from '../../../utils/openMailBox';
import { Link as MatLink } from '@mui/material';
import { setFullStoryIdentity } from '../api/account.api';
import MemberWebAuthDisabled from './MemberWebAuthDisabled';

type AuthTabType = 'login' | 'signup';

const Auth: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { snackbar, showSuccessSnackbar, showErrorSnackbar } = useSnackbar();
  const {
    verification,
    isAuthenticated,
    token,
    isSendingCode,
    isVerifyingCode,
    error,
    success,
    sendVerificationCode,
    sendMemberSignup,
    verifyMFACode,
    verifyPhone,
    sendEmailVerification,
    completeSignUpVerification,
    hasApiError,
    getApiErrorBySource,
  } = useAuth();

  const [activeTab, setActiveTab] = useState<AuthTabType>('login');
  const [localErrorMessage, setLocalErrorMessage] = useState('');
  const [isNavigating, setIsNavigating] = useState(false);
  const [emailValidationError, setEmailValidationError] = useState('');

  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const theme = useTheme();
  const hash = location.hash.replace('#', '');
  const userStatus = useSelector((state: RootState) => state.auth.userStatus);

  useEffect(() => {
    if (hash.startsWith('verification-')) {
      const authType = hash.replace('verification-', '');
      setActiveTab(authType === 'signup' ? 'signup' : 'login');
    } else if (hash === 'signup') {
      setActiveTab('signup');
    } else if (hash === 'login' || hash === '') {
      setActiveTab('login');
      if (!location.hash) {
        navigate('/auth#login', { replace: true });
      }
    }
  }, [hash, location.hash, navigate]);

  useEffect(() => {
    dispatch(setAuthFlow(activeTab));
  }, [activeTab, dispatch]);

  useEffect(() => {
    if (
      verification.userId &&
      verification.stage !== 'initial' &&
      !hash.startsWith('verification-')
    ) {
      navigate(`/auth#verification-${activeTab}`, { replace: true });
    }
  }, [verification.userId, verification.stage, hash, navigate, activeTab]);



  useEffect(() => {
    if (verification.stage === 'complete' &&
      ((verification.phoneVerified && verification.emailVerified) ||
        (activeTab === 'login' && verification.phoneVerified))) {
      showSuccessSnackbar('Verification complete! Redirecting...');

      setTimeout(() => {
        if (isAuthenticated && token) {
          setFullStoryIdentity(verification);
          // Navigate to root route - StatusRouter will determine correct route based on status
          navigate('/', { replace: true });
        }
      }, 1500);
    }
  }, [verification.stage, verification.phoneVerified, verification.emailVerified,
    activeTab, navigate, showSuccessSnackbar, isAuthenticated, token]);

  const resetErrorState = useCallback(() => dispatch(clearError()), [dispatch]);
  const resetSuccessState = useCallback(() => dispatch(clearSuccess()), [dispatch]);
  const resetAllApiErrorStates = useCallback(() => dispatch(clearAllApiErrors()), [dispatch]);
  const resetAllInput = useCallback(() => dispatch(resetAll()), [dispatch])

  const clearAllStates = useCallback(() => {
    resetErrorState();
    resetSuccessState();
    resetAllApiErrorStates();
    setLocalErrorMessage('');
    setEmailValidationError('');


    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
      navigationTimeoutRef.current = null;
    }
  }, [resetErrorState, resetSuccessState, resetAllApiErrorStates]);

  useEffect(() => {
    return () => {
      clearAllStates();
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, [clearAllStates]);

  const handleTabChange = useCallback(
    (tab: AuthTabType) => {
      if (isNavigating) return;
      resetAllInput();
      clearAllStates();
      setActiveTab(tab);
      navigate(`/auth#${tab}`, { replace: true });
    },
    [navigate, clearAllStates, isNavigating, resetAllInput]
  );

  useEffect(() => {
    if (success?.isSuccess) {
      showSuccessSnackbar(success.message);
    }
  }, [success, showSuccessSnackbar]);

  useEffect(() => {
    if (error?.hasError) {
      showErrorSnackbar(error.message);
      setLocalErrorMessage(error.message);
    }
  }, [error, showErrorSnackbar]);

  const sendCodeError = hasApiError(ErrorSource.SEND_CODE_VERIFICATION)
    ? getApiErrorBySource(ErrorSource.SEND_CODE_VERIFICATION)
    : null;

  const verifyCodeError = hasApiError(ErrorSource.VERIFY_CODE)
    ? getApiErrorBySource(ErrorSource.VERIFY_CODE)
    : null;

  const memberSignupError = hasApiError(ErrorSource.MEMBER_SIGNUP)
    ? getApiErrorBySource(ErrorSource.MEMBER_SIGNUP)
    : null;

  const sendEmailError = hasApiError(ErrorSource.SEND_EMAIL_VERIFICATION)
    ? getApiErrorBySource(ErrorSource.SEND_EMAIL_VERIFICATION)
    : null;

  const completeSignupError = hasApiError(ErrorSource.COMPLETE_SIGNUP)
    ? getApiErrorBySource(ErrorSource.COMPLETE_SIGNUP)
    : null;

  // Capture API errors for display
  useEffect(() => {
    if (sendCodeError || memberSignupError || sendEmailError || completeSignupError) {
      const error = sendCodeError || memberSignupError || sendEmailError || completeSignupError;
      setLocalErrorMessage(error?.message || '');
      setIsNavigating(false);
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
      if (error?.message.toLocaleLowerCase() === DEACTIVATED_ACCOUNT_ERROR_MESSAGE.toLocaleLowerCase()) {
        dispatch(setUserStatus(UserStatus.DELETED_BY_ADMIN));
        return
      }
    }
  }, [sendCodeError, memberSignupError, sendEmailError, completeSignupError]);

  useEffect(() => {
    if (verifyCodeError) {
      setLocalErrorMessage(verifyCodeError.message);
      setIsNavigating(false);
    }
  }, [verifyCodeError]);

  const formatPhoneNumber = useCallback((phoneNumber: string) => {
    return phoneNumber.startsWith('+1') ? phoneNumber : `+1${phoneNumber}`;
  }, []);

  const validateEmail = (email: string): boolean => {
    if (!email && activeTab === 'signup') {
      setEmailValidationError('Email is required for signup');
      return false;
    }

    if (email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setEmailValidationError('Please enter a valid email address');
        return false;
      }
    }

    setEmailValidationError('');
    return true;
  };

  const handleSendVerificationCode = useCallback(
    async (phoneNumber: string, email?: string) => {
      if (!phoneNumber || isSendingCode || isNavigating) return;

      if (activeTab === 'signup') {
        if (!validateEmail(email || '')) {
          return;
        }
      }

      clearAllStates();
      setIsNavigating(true);

      try {
        const formattedNumber = formatPhoneNumber(phoneNumber);

        if (activeTab === 'login') {
          await sendVerificationCode({ phoneNumber: formattedNumber });
        } else if (activeTab === 'signup') {
          await sendMemberSignup({
            phoneNumber: formattedNumber,
            email: email || ''
          });
        }

        const activeTabError = activeTab === 'login'
          ? hasApiError(ErrorSource.SEND_CODE_VERIFICATION)
          : hasApiError(ErrorSource.MEMBER_SIGNUP);

        const hasAnyError = activeTabError ||
          hasApiError(ErrorSource.AUTHENTICATION) ||
          error?.hasError;

        if (!hasAnyError) {
          navigate(`/auth#verification-${activeTab}`, { replace: true });
          navigationTimeoutRef.current = setTimeout(() => {
            setIsNavigating(false);
          }, 300);
        } else {
          setIsNavigating(false);
        }
      } catch (error) {
        console.error('Error sending verification code:', error);
        setIsNavigating(false);

      }
    },
    [
      isSendingCode, sendVerificationCode, sendMemberSignup, activeTab,
      navigate, clearAllStates, formatPhoneNumber, hasApiError, error, isNavigating
    ]
  );

  const handleVerifyCode = useCallback(
    async (code: string) => {
      if (!code || !verification.userId || isVerifyingCode || isNavigating) return;

      clearAllStates();
      setFullStoryIdentity(verification);
      setIsNavigating(true);


      try {
        if (activeTab === 'login') {
          await verifyMFACode({
            code,
            userId: verification.userId
          });
        } else {
          await verifyPhone({
            code,
            userId: verification.userId,
          });
        }

        if (hasApiError(ErrorSource.VERIFY_CODE) || error?.hasError) {
          setIsNavigating(false);
        } else {
          setTimeout(() => {
            setIsNavigating(false);
          }, 300);
        }
      } catch (error) {
        console.log('Error in verifying the phone', error);
        setIsNavigating(false);
      }
    },
    [
      verification.userId, isVerifyingCode, verifyMFACode, verifyPhone, activeTab,
      clearAllStates, hasApiError, error, isNavigating
    ]
  );

  const handleSendEmailVerification = useCallback(async (email: string) => {
    if (!verification.userId || (!verification.email && !email) || isSendingCode || isNavigating) return;


    clearAllStates();
    setIsNavigating(true);

    try {
      await sendEmailVerification({
        userId: verification.userId,
        email: email || verification.email
      });

      if (hasApiError(ErrorSource.SEND_EMAIL_VERIFICATION) || error?.hasError) {
        setIsNavigating(false);
      } else {
        setTimeout(() => {
          setIsNavigating(false);
        }, 300);
      }
    } catch (error) {
      console.error('Error sending email verification:', error);
      setIsNavigating(false);

    }
  }, [
    verification.userId, verification.email, isSendingCode, sendEmailVerification,
    clearAllStates, hasApiError, error, isNavigating
  ]);

  const handleVerifyEmailCode = useCallback(
    async (code: string) => {
      if (!code || !verification.userId || isVerifyingCode || isNavigating) return;

      clearAllStates();
      setIsNavigating(true);

      try {
        await completeSignUpVerification({
          code,
          userId: verification.userId
        });

        if (hasApiError(ErrorSource.COMPLETE_SIGNUP) || error?.hasError) {
          setIsNavigating(false);
        } else {
          setTimeout(() => {
            setIsNavigating(false);
          }, 300);
        }
      } catch (error) {
        console.error('Error verifying email code:', error);
        setIsNavigating(false);

      }
    },
    [
      verification.userId, isVerifyingCode, completeSignUpVerification,
      clearAllStates, hasApiError, error, isNavigating
    ]
  );

  const handleResendVerificationCode = useCallback(async () => {
    if (isNavigating) return;

    if (!verification.phoneNumber) return;

    clearAllStates();

    try {
      await sendVerificationCode({ phoneNumber: verification.phoneNumber, email: verification.email });

      if (!hasApiError(ErrorSource.SEND_CODE_VERIFICATION) && !error?.hasError) {
        showSuccessSnackbar(`Verification code sent to your phone successfully!`);
      }
    } catch (error) {
      console.error('Error resending verification code:', error);
    }
  }, [
    verification.phoneNumber, sendVerificationCode, verification.email,
    clearAllStates, hasApiError, error, showSuccessSnackbar, isNavigating
  ]);

  const handleResendEmailVerification = useCallback(async () => {
    if (isNavigating) return;

    if (!verification.userId || !verification.email) return;

    clearAllStates();

    try {
      await sendEmailVerification({
        userId: verification.userId,
        email: verification.email
      });

      if (!hasApiError(ErrorSource.SEND_EMAIL_VERIFICATION) && !error?.hasError) {
        showSuccessSnackbar(`Verification code sent to your email successfully!`);
      }
    } catch (error) {
      console.error('Error resending email verification code:', error);

    }
  }, [
    verification.userId, verification.email, sendEmailVerification,
    clearAllStates, hasApiError, error, showSuccessSnackbar, isNavigating
  ]);

  const handleChangeContactValue = useCallback((verificationType: string) => {
    if (isNavigating) return;
    if (activeTab === 'login' && verificationType !== 'phone' || activeTab === 'signup' && verificationType === 'email') {
      dispatch(setPhoneVerified());
      navigate(`/auth#${activeTab}`, { replace: true });
      return
    }
    clearAllStates();
    dispatch(resetVerification());
    navigate(`/auth#${activeTab}`, { replace: true });
  }, [dispatch, activeTab, navigate, clearAllStates, isNavigating]);

  const handleDismissError = useCallback(() => {
    setLocalErrorMessage('');
    setEmailValidationError('');
    resetAllApiErrorStates();
    resetErrorState();
  }, [resetAllApiErrorStates, resetErrorState]);

  const getVerificationScreen = () => {
    switch (verification.stage) {
      case 'initial':
        return (
          <PhoneInputStep
            activeTab={activeTab}
            isSendingCode={isSendingCode || isNavigating}
            onSendVerificationCode={handleSendVerificationCode}
            emailValidationError={emailValidationError}
            emailRequired={activeTab === 'signup'}
          />
        );

      case 'phoneVerification':
        return (
          <VerificationCodeStep
            activeTab={activeTab}
            contactValue={verification.phoneNumber}
            isVerifyingCode={isVerifyingCode || isNavigating}
            hasVerifyCodeError={!!verifyCodeError}
            onVerifyCode={handleVerifyCode}
            onResendCode={handleResendVerificationCode}
            onChangeContactValue={handleChangeContactValue}
            verificationType="phone"
            verificationHeading="Verify Your Phone"
            verificationDescription={`Enter the 6-digit code sent to ${verification.phoneNumber}`}
            buttonText={activeTab === 'login' ? 'Log In' : 'Verify Phone'}
          />
        );

      case 'emailInitiation':
        return (
          <EmailInitiationStep
            email={verification.email}
            onSendEmailCode={handleSendEmailVerification}
            isLoading={isSendingCode || isNavigating}
          />
        );

      case 'emailVerification':
        return (
          <VerificationCodeStep
            activeTab={activeTab}
            contactValue={verification.email} // Reusing field for email
            isVerifyingCode={isVerifyingCode || isNavigating}
            hasVerifyCodeError={!!completeSignupError}
            onVerifyCode={handleVerifyEmailCode}
            onResendCode={handleResendEmailVerification}
            onChangeContactValue={handleChangeContactValue}
            verificationType="email"
            verificationHeading="Verify Your Email"
            verificationDescription={`Enter the 6-digit code sent to ${verification.email} (check your spam)`}
            buttonText="Complete Sign Up"
          />
        );

      case 'complete':
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Verification Complete!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your account has been verified successfully.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Redirecting to your account...
            </Typography>
          </Box>
        );

      default:
        return (
          <PhoneInputStep
            activeTab={activeTab}
            isSendingCode={isSendingCode || isNavigating}
            onSendVerificationCode={handleSendVerificationCode}
            emailValidationError={emailValidationError}
            emailRequired={activeTab === 'signup'}
          />
        );
    }
  };


  useEffect(() => {
    if (sessionStorage.getItem('isDeletedUser')) {
      dispatch(setUserStatus(UserStatus.DELETED_BY_ADMIN));
    }
    if (sessionStorage.getItem('isExitedByAdmin')) {
      dispatch(setUserStatus(UserStatus.EXITED_BY_ADMIN));
    }
  }, []);

  // Web phone OTP retired — mobile app only (killmonger blocks API; this removes the UI).
  return <MemberWebAuthDisabled />;
};

export default React.memo(Auth);
